import {Async, Empty} from 'Core/models/Async';
import {DeletePayload} from 'Quoting/api/apiHelpers';
import {PredefinedHierarchicalContracts, ProcessingType} from 'Quoting/api/types';
import {CompensationPayload, CompensationValue, SearchCompensation} from 'Quoting/models/DTOs/CompensationPayload';
import {InitDto} from 'Quoting/models/DTOs/Init';
import {PremiumSplitDto} from 'Quoting/models/DTOs/PremiumSplit';
import {MasterPolicyQuote} from 'Quoting/models/DTOs/quote';
import {A360ProductModel} from 'Quoting/models/Quoting';
import {createAction} from 'Shared/utilities/actionCreator';

export enum CompensationActionType {
  GET_AGENCY_COMPENSATION = 'compensation/GET_AGENCY_COMPENSATION',
  UPDATE_AGENCY_COMPENSATION = 'compensation/UPDATE_AGENCY_COMPENSATION',
  GET_COMPENSATION_INIT = 'compensation/GET_COMPENSATION_INIT',
  UPDATE_COMPENSATION_INIT = 'compensation/UPDATE_COMPENSATION_INIT',
  GET_COMPENSATION_PAYLOAD = 'compensation/GET_COMPENSATION_PAYLOAD',
  UPDATE_COMPENSATION_PAYLOAD = 'compensation/UPDATE_COMPENSATION_PAYLOAD',
  SAVE_COMPENSATION_PAYLOAD = 'compensation/SAVE_COMPENSATION_PAYLOAD',
  SET_COMPENSATION_VALUE = 'compensation/SET_COMPENSATION_VALUE',
  DELETE_COMPENSATION_FLAG = 'compensation/DELETE_COMPENSATION_FLAG',
  DELETE_COMPENSATION = 'compensation/DELETE_COMPENSATION',
  SEARCH_COMPENSATION = 'compensation/SEARCH_COMPENSATION',
  SET_SEARCH_COMPENSATION = 'compensation/SET_SEARCH_COMPENSATION',
  CLEAR_COMPENSATION_DETAILS = 'compensation/CLEAR_COMPENSATION_DETAILS',
  IS_COMPENSATION_UPDATED = 'compensation/IS_COMPENSATION_UPDATED',
  SET_COMPENSATION_ERROR = 'compensation/SET_COMPENSATION_ERROR',
  GET_PREDEFINED_HIERARCHICAL_CONTRACTS = 'compensation/GET_PREDEFINED_HIERARCHICAL_CONTRACTS',
  SET_PREDEFINED_HIERARCHICAL_CONTRACTS = 'compensation/SET_PREDEFINED_HIERARCHICAL_CONTRACTS',
  REFRESH_PRODUCER_LEVELS = 'compensation/REFRESH_PRODUCER_LEVELS',
}

export const getAgencyCompensation = (quote: MasterPolicyQuote) =>
  createAction(CompensationActionType.GET_AGENCY_COMPENSATION, {quote});

export const updateAgencyCompensation = (premiumSplitSummary: Async<PremiumSplitDto>) =>
  createAction(CompensationActionType.UPDATE_AGENCY_COMPENSATION, {premiumSplitSummary});

export const getCompensationInit = () => createAction(CompensationActionType.GET_COMPENSATION_INIT, {});

export const updateCompensationInit = (initResponse: Async<InitDto>) =>
  createAction(CompensationActionType.UPDATE_COMPENSATION_INIT, {initResponse});

export const getValidatePayloadDraft = (payload: CompensationPayload) =>
  createAction(CompensationActionType.GET_COMPENSATION_PAYLOAD, {payload});

export const updateValidatePayloadDraft = (compensationPayload: Async<CompensationPayload>) =>
  createAction(CompensationActionType.UPDATE_COMPENSATION_PAYLOAD, {compensationPayload});

export const getValidatePayloadSave = (
  payload: CompensationPayload,
  shouldExit: boolean,
  opportunityId?: string,
  shouldSaveQuote?: boolean,
  onComplete?: () => void
) =>
  createAction(CompensationActionType.SAVE_COMPENSATION_PAYLOAD, {
    payload,
    shouldExit,
    opportunityId,
    shouldSaveQuote,
    onComplete,
  });

export const setCompensationValue = (compensationValue: CompensationValue) =>
  createAction(CompensationActionType.SET_COMPENSATION_VALUE, {compensationValue});

export const deleteCompensationFlag = (deleteFlag: boolean) =>
  createAction(CompensationActionType.DELETE_COMPENSATION_FLAG, {deleteFlag});

export const deleteCompensation = (payload: DeletePayload, shouldExit: boolean, opportunityId?: string) =>
  createAction(CompensationActionType.DELETE_COMPENSATION, {payload, shouldExit, opportunityId});

export const getExistingCompensationDetails = (payload: SearchCompensation, quote: MasterPolicyQuote) =>
  createAction(CompensationActionType.SEARCH_COMPENSATION, {payload, quote});

export const setExistingCompensationDetails = (searchValue: Async<CompensationPayload[]>) =>
  createAction(CompensationActionType.SET_SEARCH_COMPENSATION, {searchValue});

export const setIsCompensationUpdated = (isCompensationUpdated: boolean) =>
  createAction(CompensationActionType.IS_COMPENSATION_UPDATED, {isCompensationUpdated});

export const setCompensationError = (compensationError: string | undefined) =>
  createAction(CompensationActionType.SET_COMPENSATION_ERROR, {compensationError});

export const clearCompensationDetails = () => createAction(CompensationActionType.CLEAR_COMPENSATION_DETAILS);

export const getPredefinedHierarchicalContracts = (quote: any) =>
  createAction(CompensationActionType.GET_PREDEFINED_HIERARCHICAL_CONTRACTS, {quote});

export const setPredefinedHierarchicalContracts = (
  predefinedHierarchicalContracts: PredefinedHierarchicalContracts,
  processingType: ProcessingType
) =>
  createAction(CompensationActionType.SET_PREDEFINED_HIERARCHICAL_CONTRACTS, {
    predefinedHierarchicalContracts,
    processingType,
  });

export const refreshProducerLevels = (productModel: A360ProductModel, quotedCommissionSeries: string) =>
  createAction(CompensationActionType.REFRESH_PRODUCER_LEVELS, {productModel, quotedCommissionSeries});

type CompensationAction =
  | ReturnType<typeof getAgencyCompensation>
  | ReturnType<typeof updateAgencyCompensation>
  | ReturnType<typeof getCompensationInit>
  | ReturnType<typeof updateCompensationInit>
  | ReturnType<typeof getValidatePayloadDraft>
  | ReturnType<typeof updateValidatePayloadDraft>
  | ReturnType<typeof getValidatePayloadSave>
  | ReturnType<typeof setCompensationValue>
  | ReturnType<typeof deleteCompensationFlag>
  | ReturnType<typeof deleteCompensation>
  | ReturnType<typeof getExistingCompensationDetails>
  | ReturnType<typeof clearCompensationDetails>
  | ReturnType<typeof setIsCompensationUpdated>
  | ReturnType<typeof setCompensationError>
  | ReturnType<typeof setExistingCompensationDetails>
  | ReturnType<typeof setPredefinedHierarchicalContracts>;

type CompensationState = {
  premiumSplitSummary: Async<PremiumSplitDto>;
  initResponse: Async<InitDto>;
  compensationPayload: Async<CompensationPayload>;
  compensationValue: CompensationValue;
  deleteFlag: boolean;
  searchValue: Async<[] | CompensationPayload[]>;
  isCompensationUpdated: boolean;
  compensationError?: string | false;
  processingType?: ProcessingType;
  predefinedHierarchicalContracts?: PredefinedHierarchicalContracts;
};

const DEFAULT_COMPENSATION_STATE: CompensationState = {
  premiumSplitSummary: new Empty(),
  initResponse: new Empty(),
  compensationPayload: new Empty(),
  compensationValue: {type: '', rate: ''},
  deleteFlag: false,
  searchValue: new Empty(),
  isCompensationUpdated: false,
  compensationError: undefined,
  processingType: undefined,
  predefinedHierarchicalContracts: undefined,
};

const compensationReducer = (
  state: CompensationState = DEFAULT_COMPENSATION_STATE,
  action: CompensationAction
): CompensationState => {
  switch (action.type) {
    case CompensationActionType.UPDATE_AGENCY_COMPENSATION:
      return {
        ...state,
        premiumSplitSummary: action.payload.premiumSplitSummary,
      };

    case CompensationActionType.UPDATE_COMPENSATION_INIT:
      return {
        ...state,
        initResponse: action.payload.initResponse,
      };
    case CompensationActionType.UPDATE_COMPENSATION_PAYLOAD:
      return {
        ...state,
        compensationPayload: action.payload.compensationPayload,
      };
    case CompensationActionType.SET_COMPENSATION_VALUE:
      return {
        ...state,
        compensationValue: action.payload.compensationValue,
      };
    case CompensationActionType.DELETE_COMPENSATION_FLAG:
      return {
        ...state,
        deleteFlag: action.payload.deleteFlag,
      };
    case CompensationActionType.SET_SEARCH_COMPENSATION:
      return {
        ...state,
        searchValue: action.payload.searchValue,
      };
    case CompensationActionType.IS_COMPENSATION_UPDATED:
      return {
        ...state,
        isCompensationUpdated: action.payload.isCompensationUpdated,
      };
    case CompensationActionType.SET_COMPENSATION_ERROR:
      return {
        ...state,
        compensationError: action.payload.compensationError,
      };

    case CompensationActionType.CLEAR_COMPENSATION_DETAILS:
      return DEFAULT_COMPENSATION_STATE;

    case CompensationActionType.SET_PREDEFINED_HIERARCHICAL_CONTRACTS: {
      return {
        ...state,
        processingType: action.payload.processingType,
        predefinedHierarchicalContracts: action.payload.predefinedHierarchicalContracts,
      };
    }
    default:
      return state;
  }
};

export default compensationReducer;
